import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CloseIcon from './icons/CloseIcon';

const linkedCardSchema = z.object({
  name: z
    .string()
    .min(1, 'Card name is required')
    .max(100, 'Card name must be less than 100 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
});

type LinkedCardFormData = z.infer<typeof linkedCardSchema>;

interface CreateLinkedCardModalProps {
  parentCardName: string;
  onClose: () => void;
  onSubmit: (name: string, description: string) => void;
}

export function CreateLinkedCardModal({
  parentCardName,
  onClose,
  onSubmit,
}: CreateLinkedCardModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LinkedCardFormData>({
    resolver: zodResolver(linkedCardSchema),
  });

  const onFormSubmit = async (data: LinkedCardFormData) => {
    await onSubmit(data.name.trim(), data.description?.trim() ?? '');
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-700">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white">Create Linked Card</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <span className="w-5 h-5">
                <CloseIcon />
              </span>
            </button>
          </div>

          <p className="text-gray-400 text-sm mb-4">
            This card will be linked as a child of{' '}
            <span className="text-white font-medium">{parentCardName}</span>
          </p>

          <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Card Name <span className="text-red-400">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter card name..."
                autoFocus
              />
              {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="description"
                {...register('description')}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y min-h-[80px]"
                placeholder="Add a description..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create & Link'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
